import React from 'react';

import { usePostModuleMutation } from 'src/redux/services/centralApi';
import { Button, Checkbox, Flex, Form, Input, Typography } from 'antd';
import type { Module } from 'src/interfaces/entities/module';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { MinusCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import SelectTeam from 'src/pages/observers/module/components/SelectTeam';
import SelectTarget from 'src/pages/observers/module/components/SelectTarget';
import SelectIcon from 'src/pages/observers/module/components/SelectIcon';

interface IAddModuleForm extends Omit<Module, 'module_id' | 'metadata'> {
	metadata: { key: string, value: string }[]
}

export default function AddModulePage() {
	const navigate = useNavigate();

	const [ postModule, ] = usePostModuleMutation();

	const [ addModuleForm, ] = Form.useForm<IAddModuleForm>();

	const onFinishAddModuleForm = (module: IAddModuleForm) => {
		const metadata: Record<string, string> = {};
		module.metadata?.forEach(({ key, value, }) => {
			metadata[key] = value;
		});

		const creation_date = dayjs().format('YYYY-MM-DD');

		postModule({
			...module,
			metadata,
			creation_date,
		})
			.unwrap()
			.then(() => {
				toast.success('Módulo criado com sucesso!', {
					autoClose: 1000,
				});
				navigate('/observador/modulo/listar');
			})
			.catch((err) => {
				if (err?.status && err?.status === 500
                    && err?.data?.frontend_message) {
					toast.error(err?.data?.frontend_message, {
						autoClose: 1000,
					});
				}
			})
			.finally(() => {
				addModuleForm.resetFields();
			});
	};

	return <DashboardTemplate pageTitle="Adicionar Módulo">
		<Form
			layout="vertical"
			onFinish={onFinishAddModuleForm}
			form={addModuleForm}
			style={{ marginTop: 10, }}
		>
			<Form.Item
				name={'name'}
				hasFeedback
				required
				label={'Nome do módulo'}
				rules={[{
					required: true,
					message: 'Campo obrigatório.',
				},]}
			>
				<Input placeholder="Nome do módulo" />
			</Form.Item>
			<Form.Item
				name={'icon'}
				hasFeedback
				tooltip={'Ícone de identificação do módulo'}
				label={'Ícone'}
				rules={[{
					required: true,
					message: 'Campo obrigatório.',
				},]}
			>
				<SelectIcon
					onIconChange={(value) =>
						addModuleForm.setFieldValue('icon', value)
					}
				/>
			</Form.Item>
			<Form.Item
				name={'short_description'}
				hasFeedback
				tooltip={'Uma breve descrição exibida no preview do módulo.'}
				label={'Breve descrição'}
				help={'Máximo de caracteres: 128'}
				rules={[
					{
						required: true,
						message: 'Campo obrigatório.',
					},
					{
						max: 128,
						message: 'Máximo de caracteres: 128',
					},
				]}
			>
				<Input maxLength={128} placeholder="Breve descrição" />
			</Form.Item>
			<Form.Item
				name={'long_description'}
				hasFeedback
				tooltip={'Uma descrição exibida ao abrir o módulo.'}
				label={'Descrição detalhada'}
				rules={[{
					required: true,
					message: 'Campo obrigatório.',
				},]}
			>
				<Input.TextArea placeholder="Descrição detalhada" />
			</Form.Item>
			<Form.Item
				name={'team'}
				hasFeedback
				tooltip={'Informe o time que desenvolverá os observadores do módulo.'}
				label={'Time'}
				rules={[{
					required: true,
					message: 'Campo obrigatório.',
				},]}
			>
				<SelectTeam />
			</Form.Item>
			<Form.Item
				name={'target'}
				hasFeedback
				tooltip={'Informe o ERP dos observadores do módulo.'}
				label={'ERP'}
				rules={[{
					required: true,
					message: 'Campo obrigatório.',
				},]}
			>
				<SelectTarget />
			</Form.Item>
			<Typography.Text
				style={{ paddingBottom: '8px', }}
			>
                Campos personalizados
			</Typography.Text>
			<Form.List name="metadata">
				{(fields, { add, remove, }) => (
					<>
						{fields.map(({ key, name, ...restField }) => (
							<Flex
								gap={8}
								key={key}
								style={{ marginBottom: 8, }}
								align="center"
							>
								<Form.Item
									{...restField}
									name={[ name, 'key', ]}
									rules={[{
										required: true,
										message: 'Campo obrigatório.',
									},]}
								>
									<Input placeholder="Campo" />
								</Form.Item>
								<Form.Item
									{...restField}
									name={[ name, 'value', ]}
									rules={[{
										required: true,
										message: 'Campo obrigatório.',
									},]}
									style={{ width: '100%', }}
								>
									<Input placeholder="Valor" />
								</Form.Item>
								<MinusCircleOutlined
									style={{ marginBottom: '24px', }}
									onClick={() => remove(name)}
								/>
							</Flex>
						))}
						<Form.Item>
							<Button
								type="dashed"
								onClick={() => add()}
								block
								icon={<PlusOutlined />}
							>
                                Novo campo personalizado
							</Button>
						</Form.Item>
					</>
				)}
			</Form.List>
			<hr />
			<Form.Item
				initialValue={true}
				name='is_active'
				valuePropName={'checked'}
				hasFeedback
			>
				<Checkbox>Módulo ativo</Checkbox>
			</Form.Item>
			<Flex justify="flex-end" gap={8}>
				<Button
					htmlType="reset"
					type="text"
					icon={<ReloadOutlined />}
				/>
				<Button
					onClick={() => {
						addModuleForm.submit();
					}}
					type="primary"
				>
                    Criar módulo
				</Button>
			</Flex>
		</Form>
	</DashboardTemplate>;
}